/**
 * Neuron Report Dispatch — Cognito hosted-UI authentication (authorization code + PKCE).
 *
 * The app client is PUBLIC and has NO client secret. A secret cannot be shipped in a
 * bundle, so PKCE is the whole of the proof: we generate a random verifier, send only
 * its SHA-256 challenge to /oauth2/authorize, and present the verifier at /oauth2/token.
 *
 * CONTRACT §8.6: a token is never logged. Everything that reaches console.* here goes
 * through format.maskToken(). Do not add a debug line that prints a raw token, not even
 * temporarily — bundles get deployed with those in them.
 *
 * Usage from screens: you almost never call this directly. `RequireAuth` in router.tsx
 * runs the guard, and `lib/api.ts` attaches the access token to every request and
 * retries once through refreshSession() on a 401. What screens DO use:
 *
 *   getIdentity()    -> the signed-in operator, for audit copy ("Revoked by …")
 *   signOut()        -> the user menu
 *   useAuth()        -> reactive { status, identity } for the top bar
 */

import { useEffect, useMemo, useState } from 'react';

import { maskToken, safeLog } from './format';
import type { Identity } from './types';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

export interface AuthConfig {
  domain: string;
  clientId: string;
  redirectUri: string;
  logoutUri: string;
  scopes: string;
  region: string;
  userPoolId: string;
  disabled: boolean;
}

const env = import.meta.env;

function trimSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

export const authConfig: AuthConfig = {
  domain: trimSlash(String(env.VITE_COGNITO_DOMAIN ?? '')),
  clientId: String(env.VITE_COGNITO_CLIENT_ID ?? ''),
  redirectUri: String(
    env.VITE_COGNITO_REDIRECT_URI ??
      (typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : ''),
  ),
  logoutUri: String(
    env.VITE_COGNITO_LOGOUT_URI ?? (typeof window !== 'undefined' ? `${window.location.origin}/` : ''),
  ),
  scopes: String(env.VITE_COGNITO_SCOPES ?? 'openid email profile'),
  region: String(env.VITE_COGNITO_REGION ?? 'ap-south-1'),
  userPoolId: String(env.VITE_COGNITO_USER_POOL_ID ?? ''),
  disabled: String(env.VITE_AUTH_DISABLED ?? '0') === '1',
};

/** False when the deployment has not been wired up yet — screens show a setup notice, not a crash. */
export function isConfigured(): boolean {
  return Boolean(authConfig.domain && authConfig.clientId);
}

// ---------------------------------------------------------------------------
// Session storage
// ---------------------------------------------------------------------------

const SESSION_KEY = 'nrd.auth.v1';
const PKCE_KEY = 'nrd.auth.pkce.v1';

/** Refresh this far ahead of expiry so an in-flight request never races the clock. */
const REFRESH_SKEW_MS = 120_000;

export interface Session {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  token_type: string;
  /** Absolute epoch ms at which access_token stops being valid. */
  expires_at: number;
  obtained_at: number;
}

export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

interface PkceState {
  verifier: string;
  state: string;
  returnTo: string;
  created_at: number;
}

function readSession(): Session | null {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    if (!parsed || typeof parsed.access_token !== 'string' || !parsed.access_token) return null;
    return parsed;
  } catch {
    // Private mode, blocked storage, or corrupt JSON: treat as signed out.
    return null;
  }
}

function writeSession(session: Session | null): void {
  try {
    if (session) window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else window.localStorage.removeItem(SESSION_KEY);
  } catch {
    /* storage unavailable: the session lives only in this tab's memory cache */
  }
  cached = session;
  notify();
}

/**
 * Install a session obtained outside the OAuth redirect flow.
 *
 * The console signs in on its own page (pages/Login.tsx) against Cognito's SRP
 * flow rather than bouncing to the Hosted UI, so the tokens arrive here instead
 * of from completeSignIn(). Everything downstream — the guard, the top bar,
 * cross-tab sign-out, refresh — reads the same stored session either way.
 */
export function installSession(session: Session | null): void {
  writeSession(session);
}

let cached: Session | null | undefined;

function currentSession(): Session | null {
  if (cached === undefined) cached = readSession();
  return cached;
}

// ---------------------------------------------------------------------------
// Subscriptions (top bar, guards, cross-tab sign-out)
// ---------------------------------------------------------------------------

type Listener = (session: Session | null) => void;
const listeners = new Set<Listener>();

function notify(): void {
  const session = currentSession();
  for (const fn of listeners) {
    try {
      fn(session);
    } catch {
      /* a listener throwing must not break the others */
    }
  }
}

/** Subscribe to sign-in/sign-out. Returns an unsubscribe function. */
export function onAuthChange(listener: Listener): () => void {
  listeners.add(listener);
  // Returns void deliberately: React's useEffect destructor type rejects a boolean.
  return () => {
    listeners.delete(listener);
  };
}

if (typeof window !== 'undefined') {
  // Signing out in one tab signs out the rest.
  window.addEventListener('storage', (event) => {
    if (event.key === SESSION_KEY) {
      cached = readSession();
      notify();
    }
  });
}

// ---------------------------------------------------------------------------
// PKCE primitives
// ---------------------------------------------------------------------------

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomUrlSafe(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function sha256Challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

function storePkce(state: PkceState): void {
  try {
    window.sessionStorage.setItem(PKCE_KEY, JSON.stringify(state));
  } catch {
    safeLog('auth', 'session storage unavailable; PKCE state cannot be persisted');
  }
}

function takePkce(): PkceState | null {
  try {
    const raw = window.sessionStorage.getItem(PKCE_KEY);
    window.sessionStorage.removeItem(PKCE_KEY);
    return raw ? (JSON.parse(raw) as PkceState) : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// JWT
// ---------------------------------------------------------------------------

/**
 * Decode a JWT payload. This does NOT verify the signature — the API does that, and a
 * browser-side check would be theatre. Used only for display (email) and expiry hints.
 */
export function decodeJwt<T = Record<string, unknown>>(token: string): T | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
    return JSON.parse(decodeURIComponent(escape(json))) as T;
  } catch {
    return null;
  }
}

interface IdTokenClaims {
  sub?: string;
  email?: string;
  name?: string;
  'cognito:username'?: string;
  'cognito:groups'?: string[];
  exp?: number;
}

/** The signed-in operator, or null. */
const DEV_IDENTITY: Identity = {
  sub: 'dev',
  email: 'dev@localhost',
  name: 'Local dev',
  groups: ['admin'],
};

export function getIdentity(): Identity | null {
  const session = currentSession();
  // VITE_AUTH_DISABLED=1: stand in for a real operator so the chrome renders.
  if (authConfig.disabled && (!session || session.id_token === 'dev')) return DEV_IDENTITY;
  if (!session) return null;
  const claims = decodeJwt<IdTokenClaims>(session.id_token);
  if (!claims) return null;
  return {
    sub: claims.sub ?? '',
    email: claims.email ?? '',
    name: claims.name,
    username: claims['cognito:username'],
    groups: claims['cognito:groups'] ?? [],
  };
}

/** Group membership check for the few admin-only actions (revoke, client create). */
export function hasGroup(group: string): boolean {
  return getIdentity()?.groups.includes(group) ?? false;
}

// ---------------------------------------------------------------------------
// Sign in
// ---------------------------------------------------------------------------

export function getSession(): Session | null {
  return currentSession();
}

/** True when a usable session exists — either unexpired, or refreshable. */
export function isAuthenticated(): boolean {
  if (authConfig.disabled) return true;
  const session = currentSession();
  if (!session) return false;
  if (session.expires_at > Date.now()) return true;
  return Boolean(session.refresh_token);
}

/**
 * Send the operator to the Cognito hosted UI. `returnTo` defaults to the current
 * location so a deep link (/runs/abc123) survives the round trip.
 *
 * This navigates away from the page; nothing after it runs.
 */
export async function beginSignIn(returnTo?: string): Promise<void> {
  if (authConfig.disabled) {
    writeSession({
      access_token: 'dev',
      id_token: 'dev',
      token_type: 'Bearer',
      expires_at: Date.now() + 86_400_000,
      obtained_at: Date.now(),
    });
    return;
  }
  if (!isConfigured()) {
    throw new Error(
      'Cognito is not configured. Set VITE_COGNITO_DOMAIN and VITE_COGNITO_CLIENT_ID in .env.local.',
    );
  }

  const verifier = randomUrlSafe(48);
  const state = randomUrlSafe(16);
  const challenge = await sha256Challenge(verifier);

  storePkce({
    verifier,
    state,
    returnTo: returnTo ?? `${window.location.pathname}${window.location.search}`,
    created_at: Date.now(),
  });

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: authConfig.clientId,
    redirect_uri: authConfig.redirectUri,
    scope: authConfig.scopes,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.assign(`${authConfig.domain}/oauth2/authorize?${params.toString()}`);
}

export interface SignInResult {
  /** Where the operator was headed before being bounced to the hosted UI. */
  returnTo: string;
}

/**
 * Finish the hosted-UI round trip. Call once, from the /auth/callback route.
 *
 * Throws with a readable message on a state mismatch (possible CSRF, or a stale tab)
 * or when Cognito returns `?error=`.
 */
export async function completeSignIn(): Promise<SignInResult> {
  if (authConfig.disabled) return { returnTo: '/' };

  const url = new URL(window.location.href);
  const error = url.searchParams.get('error');
  if (error) {
    const description = url.searchParams.get('error_description') ?? '';
    throw new Error(`Sign-in was rejected: ${error}${description ? ` — ${description}` : ''}`);
  }

  const code = url.searchParams.get('code');
  const state = url.searchParams.get('state');
  if (!code) throw new Error('Sign-in callback is missing its authorization code.');

  const pkce = takePkce();
  if (!pkce) {
    throw new Error(
      'This sign-in could not be matched to a request from this browser. Start again from the console.',
    );
  }
  if (pkce.state !== state) {
    throw new Error('Sign-in state did not match. Start again from the console.');
  }

  const session = await exchange({
    grant_type: 'authorization_code',
    client_id: authConfig.clientId,
    code,
    redirect_uri: authConfig.redirectUri,
    code_verifier: pkce.verifier,
  });

  writeSession(session);
  safeLog('auth', 'signed in', { token: maskToken(session.access_token) });

  return { returnTo: pkce.returnTo || '/' };
}

// ---------------------------------------------------------------------------
// Token exchange and refresh
// ---------------------------------------------------------------------------

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

async function exchange(body: Record<string, string>): Promise<Session> {
  const response = await fetch(`${authConfig.domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body).toString(),
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const payload = (await response.json()) as { error?: string; error_description?: string };
      detail = payload.error_description || payload.error || detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(`Token request failed (${response.status}): ${detail}`);
  }

  const payload = (await response.json()) as TokenResponse;
  const now = Date.now();
  return {
    access_token: payload.access_token,
    id_token: payload.id_token,
    // Cognito omits refresh_token on a refresh; keep the one we already hold.
    refresh_token: payload.refresh_token ?? currentSession()?.refresh_token,
    token_type: payload.token_type || 'Bearer',
    expires_at: now + (payload.expires_in ?? 3600) * 1000,
    obtained_at: now,
  };
}

let refreshInFlight: Promise<Session | null> | null = null;

/**
 * Refresh the access token. Single-flight: ten concurrent 401s produce one refresh
 * call, and all ten await the same promise.
 *
 * Returns null when there is nothing to refresh with — the caller should sign in.
 */
export function refreshSession(): Promise<Session | null> {
  if (authConfig.disabled) return Promise.resolve(currentSession());
  if (refreshInFlight) return refreshInFlight;

  const session = currentSession();
  if (!session?.refresh_token) return Promise.resolve(null);

  refreshInFlight = exchange({
    grant_type: 'refresh_token',
    client_id: authConfig.clientId,
    refresh_token: session.refresh_token,
  })
    .then((next) => {
      writeSession(next);
      safeLog('auth', 'token refreshed', { token: maskToken(next.access_token) });
      return next;
    })
    .catch((err: unknown) => {
      // A dead refresh token is a normal end-of-session, not an incident.
      safeLog('auth', 'refresh failed; session cleared', {
        reason: err instanceof Error ? err.message : 'unknown',
      });
      writeSession(null);
      return null;
    })
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

/**
 * The bearer token for an API call, refreshed if it is within REFRESH_SKEW_MS of
 * expiry. Returns null when the operator must sign in again.
 */
export async function getAccessToken(): Promise<string | null> {
  if (authConfig.disabled) return 'dev';
  const session = currentSession();
  if (!session) return null;
  if (session.expires_at - REFRESH_SKEW_MS > Date.now()) return session.access_token;
  const refreshed = await refreshSession();
  return refreshed?.access_token ?? null;
}

// ---------------------------------------------------------------------------
// Sign out
// ---------------------------------------------------------------------------

export interface SignOutOptions {
  /** Skip the hosted-UI logout redirect — used when the session simply expired. */
  local?: boolean;
}

/**
 * Clear the session and, unless `local`, bounce through the hosted UI logout endpoint
 * so the Cognito cookie goes too. Without that round trip the next sign-in silently
 * reuses the old identity, which is how someone ends up revoking a grant as the wrong
 * operator in the audit trail.
 */
export function signOut(options: SignOutOptions = {}): void {
  writeSession(null);
  try {
    window.sessionStorage.removeItem(PKCE_KEY);
  } catch {
    /* ignore */
  }
  safeLog('auth', 'signed out');

  if (options.local || authConfig.disabled || !isConfigured()) {
    window.location.assign('/');
    return;
  }

  const params = new URLSearchParams({
    client_id: authConfig.clientId,
    logout_uri: authConfig.logoutUri,
  });
  window.location.assign(`${authConfig.domain}/logout?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// React binding
// ---------------------------------------------------------------------------

export interface AuthState {
  status: AuthStatus;
  session: Session | null;
  identity: Identity | null;
}

/**
 * Reactive auth state for chrome that must re-render on sign-out (the top bar, the
 * route guard). Screens fetching data do NOT need this — lib/api.ts handles the token.
 */
export function useAuth(): AuthState {
  const [session, setSession] = useState<Session | null>(() => getSession());

  useEffect(() => onAuthChange(setSession), []);

  return useMemo<AuthState>(() => {
    const authed = authConfig.disabled || Boolean(session);
    return {
      status: authed ? 'authenticated' : 'anonymous',
      session,
      identity: authed ? getIdentity() : null,
    };
  }, [session]);
}
